import { styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { IChatConversation } from 'src/@types/chat';
import { useAuthContext } from 'src/auth/useAuthContext';
import { UncaughtError } from 'src/components/base/UncaughtError';
import { GuestActionsBar } from 'src/features/ActionsBar/GuestActionsBar';
import { usePlaySound } from 'src/features/RTCBar/GatheringSpacesSoundBoard/usePlaySound';
import { Roles } from 'src/features/workspace/WorkspaceShareMemberRoleSelectProps';
import { useLocales } from 'src/locales';
import { useChatContext } from 'src/provider/ChatContext';
import { useVoiceChannelContext } from 'src/provider/VoiceChannel';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { getConversation, getConversations } from 'src/redux/slices/chat';
import { getWorkspaceInfo } from 'src/redux/slices/workspace';
import { useDispatch } from 'src/redux/store';
import { useSocketEvent } from 'src/socket';
import { onNewSession } from 'src/socket/listeners/gathering';
import {
  onPlaySound,
  onVoiceChannelListUpdated,
  onVoiceChannelUpdated,
} from 'src/socket/listeners/voice-channel';
import { ChatVoiceChannel } from '../chat/ChatVoiceChannel';

export const GuestGatheringPage = () => {
  const { id = '' } = useParams();
  const [isJoining, setIsJoining] = useState(false);
  const [conversation, setConversation] = useState<IChatConversation | null>();

  const { logoutGuest } = useAuthContext();
  const { translate } = useLocales();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { setCurrentActiveChat, setIsDrawer } = useChatContext();
  const { connectVoiceChannel, setVoiceChannels } = useVoiceChannelContext();

  const { playSound } = usePlaySound();

  const { connectionObject, updateLocalParticipant, setActiveSessionId } =
    useLivekitRTCState();

  const externalLink = sessionStorage.getItem('externalLink');

  useEffect(() => {
    if (connectionObject) {
      dispatch(getConversations()).then();
      dispatch(getWorkspaceInfo()).then();
      dispatch(
        getConversation(id, (channel) => setConversation(channel)),
      ).then();
      return;
    }
    sessionStorage.removeItem('accessToken');
    logoutGuest();
    navigate(`/gathering/lobby/${externalLink}`);

    return () => sessionStorage.removeItem('accessToken');
  }, [id, connectionObject, externalLink, logoutGuest, navigate, dispatch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!conversation || isJoining || !connectionObject) return;
    setIsJoining(true);
    setIsDrawer(false);
    setCurrentActiveChat(id);
    connectVoiceChannel({ channel: conversation, isGuest: true });
    updateLocalParticipant({ role: Roles.VIEWER });
  }, [
    id,
    conversation,
    connectionObject,
    isJoining,
    setCurrentActiveChat,
    setIsDrawer,
  ]);

  useSocketEvent(
    onVoiceChannelListUpdated((channels) => {
      setVoiceChannels(channels);
    }),
  );

  useSocketEvent(
    onVoiceChannelUpdated((newVoiceChannel) => {
      setVoiceChannels((prevVoiceChannels) =>
        prevVoiceChannels.map((stateVoiceChannel) =>
          stateVoiceChannel.channel._id === newVoiceChannel.channel._id
            ? {
                ...newVoiceChannel,
                members: newVoiceChannel.members.map((stateMember) => ({
                  ...stateVoiceChannel.members.find(
                    (newMember) => newMember.user._id === stateMember.user._id,
                  ),
                  ...stateMember,
                  ...(!stateMember.sharing && !stateMember.camera
                    ? { theaterMode: false }
                    : {}),
                })),
              }
            : stateVoiceChannel,
        ),
      );
    }),
  );

  // VOICE SESSION
  useSocketEvent(
    onNewSession(({ session }: { session: string }) => {
      setActiveSessionId(session);
    }),
  );

  // VOICE SOUNDS
  useSocketEvent(
    onPlaySound((data) => {
      playSound(data);
    }),
  );

  if (!conversation) return null;

  return (
    <>
      <Helmet>
        <title> {translate('communicate.conference.helmet')} | Copera.ai</title>
      </Helmet>

      <ErrorBoundary FallbackComponent={UncaughtError}>
        <GuestGatheringContent>
          <ChatVoiceChannel conversation={conversation} isGuest={true} />
          <GuestActionsBar />
        </GuestGatheringContent>
      </ErrorBoundary>
    </>
  );
};

const GuestGatheringContent = styled('div')(() => ({
  display: 'flex',
  flexGrow: 1,
  width: '100%',
  gridArea: 'content',
  position: 'relative',
  overflow: 'hidden',
}));
